import { FC, useState } from 'react';
import { Center, Image, List } from '@chakra-ui/react';

import laptopImg from 'assets/brand/laptop@2x.png';
import Button from 'components/_core/Button';
import SunshineModal from 'components/_core/SunshineModal';
import Text from 'components/_core/Text';
import FilePicker from 'components/FilePicker';
import { FilePickerErrorCode } from 'components/FilePicker/types';
import RichText from 'components/RichText';
import { MEGABYTE } from 'components/Upload';
import { DropZoneEvent } from 'components/Upload/components/DropZone/types';
import type { FileMimeType } from 'components/Upload/types';

import PreviewImage from '../PreviewImage';
import UploadErrorCallout from '../UploadErrorCallout';
import { MAX_LOGO_SIZE } from './constants';
import locales from './locales';

interface PickerStepProps {
  onGoBack: () => void;
  onSelect: (file: File, eventMethod: DropZoneEvent) => void;
}

const ACCEPTED_IMAGE_TYPES: FileMimeType[] = ['image/jpeg', 'image/png'];

const PickerStep: FC<PickerStepProps> = ({ onGoBack, onSelect }) => {
  const [fileName, setFileName] = useState<string | null>(null);
  const [error, setError] = useState<FilePickerErrorCode | null>(null);

  return (
    <>
      <PreviewImage isError={Boolean(error)} />

      <SunshineModal.Body>
        <SunshineModal.Title>{locales.title}</SunshineModal.Title>

        <List marginBottom="space-24" stylePosition="inside" styleType="disc">
          <Text as="li">{locales.description.acceptedTypes}</Text>
          <Text as="li">
            {
              locales.formatString(locales.description.maxSize, {
                maxSize: MAX_LOGO_SIZE,
              }) as string
            }
          </Text>
        </List>

        {error ? (
          <UploadErrorCallout
            errorCode={error}
            fileName={fileName}
            onExtraAction={() => {
              setFileName(null);
              setError(null);
            }}
          />
        ) : (
          <FilePicker
            acceptedTypes={ACCEPTED_IMAGE_TYPES}
            maxSize={MAX_LOGO_SIZE * MEGABYTE}
            onError={(errorType, file) => {
              setFileName(file.name);
              setError(errorType);
            }}
            onSelect={onSelect}
          >
            <Center
              flexDirection="column"
              gap="space-4"
              paddingY="space-16"
              textAlign="center"
            >
              <Image
                alt=""
                boxSize="56px"
                role="presentation"
                src={laptopImg}
              />

              <Text fontWeight="weight-500" size="lg" variant="primary">
                {locales.dropZone.title}
              </Text>

              <RichText
                renderStrong={(textProps) => (
                  <Text
                    as="span"
                    {...textProps}
                    color="blue.600"
                    fontWeight="weight-500"
                    size="lg"
                  />
                )}
                size="lg"
              >
                {locales.dropZone.browse}
              </RichText>
            </Center>
          </FilePicker>
        )}
      </SunshineModal.Body>

      <SunshineModal.Footer>
        <Button
          icon="arrow-left"
          iconPosition="left"
          onClick={onGoBack}
          variant="inline-primary"
        >
          {locales.back}
        </Button>

        <Button icon="arrow-right" isDisabled>
          {locales.submit}
        </Button>
      </SunshineModal.Footer>
    </>
  );
};

export default PickerStep;
